import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Input,
  Radio,
  Select,
  Spin,
  Table,
  Tag,
} from "antd";
import {
  AppstoreOutlined,
  CheckCircleOutlined,
  ClockCircleOutlined,
  DatabaseOutlined,
  FileExcelOutlined,
  FilterOutlined,
  InfoCircleOutlined,
  ReloadOutlined,
  SafetyCertificateOutlined,
  SearchOutlined,
  UnorderedListOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import axios from "axios";
import * as XLSX from "xlsx";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { apiUrl } from "../config";
import TableContext from "../Context/TablePosition";

/**
 * Stok Yeterliliği Sayfası
 *
 * Her boyalı gövde ve gövde ön için:
 * - Mevcut stok
 * - Son yeterlilik tarihi (SAP montaj planındaki siparişlerin karşılandığı son an)
 * - Kalan süre (Saat / Dakika veya Tüm Planı Karşılıyor)
 * - Kritik / Normal durumu
 *
 * Açık (Light) ve Koyu (Dark) mod tam uyumludur.
 */
export default function StokYeterliligiPage() {
  const { t } = useTranslation();
  const location = useLocation();
  const { setCurrentUrl } = useContext(TableContext);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [dataList, setDataList] = useState([]);
  const [isLegacyPlan, setIsLegacyPlan] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);

  // Filtreler ve Görünüm
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); // ALL | CRITICAL | NORMAL | COVERS_ALL
  const [typeFilter, setTypeFilter] = useState("ALL"); // ALL | GOVDE | GOVDE_ON
  const [viewMode, setViewMode] = useState("GRID"); // GRID | TABLE

  const username = process.env.REACT_APP_USERNAME;
  const password = process.env.REACT_APP_SECRET_KEY;

  // Sayfa URL'ini context ve localStorage'a kaydet
  useEffect(() => {
    localStorage.setItem("urlpaintshop", location.pathname);
    if (setCurrentUrl) {
      setCurrentUrl(location.pathname);
    }
  }, [location.pathname, setCurrentUrl]);

  // Veri Çekme
  const fetchData = async () => {
    setLoading(true);
    setError(null);
    try {
      const isBeta = location.pathname.startsWith("/simulasyon");
      const endpoint = isBeta ? "/beta/getStockSufficiency" : "/getStockSufficiency";

      const res = await axios.get(`${apiUrl}${endpoint}`, {
        auth: { username, password },
      });

      const resData = res.data || {};

      // Legacy plan kontrolü
      const legacy =
        resData.legacy_plan === true ||
        resData.legacyPlan === true ||
        resData.is_legacy === true;
      setIsLegacyPlan(legacy);

      // Malzeme listesini normalize et
      let items = [];
      if (Array.isArray(resData.data)) {
        items = resData.data;
      } else if (Array.isArray(resData.items)) {
        items = resData.items;
      } else if (Array.isArray(resData.results)) {
        items = resData.results;
      } else if (Array.isArray(resData.sufficiency)) {
        items = resData.sufficiency;
      } else if (Array.isArray(resData)) {
        items = resData;
      }

      // Her kalemi standart formata getir
      const normalized = items.map((item, idx) => {
        const code =
          item.material_code ||
          item.mat_code ||
          item.code ||
          item.material_id ||
          item.material ||
          `MAT-${idx + 1}`;

        const name =
          item.material_name ||
          item.mat_name ||
          item.name ||
          item.description ||
          code;

        // Malzeme Tipi (Gövde Ön vs Gövde)
        let type =
          item.material_type ||
          item.type ||
          item.group ||
          item.category ||
          "";
        if (!type) {
          if (name.toLowerCase().includes("ön") || name.toLowerCase().includes("on")) {
            type = "Gövde Ön";
          } else {
            type = "Gövde";
          }
        }

        const currentStock =
          item.current_stock ??
          item.stock ??
          item.mevcut_stok ??
          item.stok ??
          0;

        const sufficiencyDateRaw =
          item.sufficiency_date ||
          item.son_yeterlilik_tarihi ||
          item.end_time ||
          item.target_date ||
          null;

        const remainingMinutes =
          item.remaining_minutes ??
          item.remaining_time_minutes ??
          item.kalan_dakika ??
          null;

        const remainingTextRaw =
          item.remaining_time_text ||
          item.remaining_time ||
          item.kalan_sure ||
          "";

        // Tüm planı karşılıyor mu?
        const coversAll =
          item.covers_entire_plan === true ||
          item.covers_all === true ||
          item.status === "SUFFICIENT" ||
          item.status === "FULL" ||
          remainingTextRaw.toLowerCase().includes("tüm") ||
          remainingTextRaw.toLowerCase().includes("tum") ||
          remainingTextRaw.toLowerCase().includes("bitmiyor") ||
          (sufficiencyDateRaw === null && remainingMinutes === null);

        // Kalan Süre Metni Hesabı (eğer backend'den gelmediyse)
        let remainingText = remainingTextRaw;
        if (!remainingText) {
          if (coversAll) {
            remainingText = "Tüm planı karşılıyor";
          } else if (remainingMinutes !== null && remainingMinutes !== undefined) {
            if (remainingMinutes <= 0) {
              remainingText = "Stok tükendi";
            } else {
              const hours = Math.floor(remainingMinutes / 60);
              const mins = Math.round(remainingMinutes % 60);
              if (hours > 0) {
                remainingText = `${hours} saat ${mins > 0 ? `${mins} dk` : ""}`.trim();
              } else {
                remainingText = `${mins} dk`;
              }
            }
          } else {
            remainingText = "-";
          }
        }

        // Kritiklik Durumu
        let isCritical = false;
        if (!coversAll) {
          if (item.is_critical !== undefined) {
            isCritical = Boolean(item.is_critical);
          } else if (item.status === "CRITICAL" || item.status === "KRITIK") {
            isCritical = true;
          } else if (remainingMinutes !== null && remainingMinutes !== undefined) {
            isCritical = remainingMinutes <= 120;
          }
        }

        return {
          id: code,
          code,
          name,
          type,
          stock: currentStock,
          sufficiencyDate: sufficiencyDateRaw,
          remainingMinutes,
          remainingText,
          coversAll,
          isCritical,
          rawItem: item,
        };
      });

      setDataList(normalized);
      setLastUpdated(new Date());
    } catch (err) {
      console.error("Stok Yeterliliği verisi çekilemedi:", err);
      setError(
        err?.response?.data?.detail ||
          err?.response?.data?.message ||
          err.message ||
          "Stok yeterliliği verisi alınırken bir hata oluştu."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [location.pathname]);

  // Filtrelenmiş Liste
  const filteredData = useMemo(() => {
    return dataList.filter((item) => {
      // Arama Filtresi
      if (searchTerm.trim()) {
        const term = searchTerm.toLowerCase().trim();
        const matchesName = item.name.toLowerCase().includes(term);
        const matchesCode = item.code.toLowerCase().includes(term);
        if (!matchesName && !matchesCode) return false;
      }

      // Tip Filtresi (Gövde vs Gövde Ön)
      if (typeFilter !== "ALL") {
        if (typeFilter === "GOVDE_ON" && !item.type.toLowerCase().includes("ön")) {
          return false;
        }
        if (typeFilter === "GOVDE" && item.type.toLowerCase().includes("ön")) {
          return false;
        }
      }

      // Durum Filtresi
      if (statusFilter === "CRITICAL" && !item.isCritical) return false;
      if (statusFilter === "NORMAL" && (item.isCritical || item.coversAll))
        return false;
      if (statusFilter === "COVERS_ALL" && !item.coversAll) return false;

      return true;
    });
  }, [dataList, searchTerm, typeFilter, statusFilter]);

  // Metrikler
  const metrics = useMemo(() => {
    const total = dataList.length;
    const criticalCount = dataList.filter((d) => d.isCritical).length;
    const coversAllCount = dataList.filter((d) => d.coversAll).length;
    const normalCount = total - criticalCount - coversAllCount;
    return { total, criticalCount, normalCount, coversAllCount };
  }, [dataList]);

  // Excel'e Aktarma
  const handleExportExcel = () => {
    if (!filteredData || filteredData.length === 0) return;

    const exportRows = filteredData.map((item) => ({
      "Malzeme Kodu": item.code,
      "Malzeme Adı": item.name,
      Tip: item.type,
      "Mevcut Stok (Adet)": item.stock,
      "Son Yeterlilik Tarihi": item.sufficiencyDate
        ? formatDate(item.sufficiencyDate)
        : "Tüm Planı Karşılıyor",
      "Kalan Süre": item.remainingText,
      Durum: item.isCritical
        ? "KRİTİK"
        : item.coversAll
        ? "Tüm Planı Karşılıyor"
        : "Normal",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportRows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Stok Yeterliliği");

    const dateStr = new Date().toISOString().split("T")[0];
    XLSX.writeFile(workbook, `Stok_Yeterliligi_${dateStr}.xlsx`);
  };

  // Tarih Formatlayıcı
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    try {
      const d = new Date(dateStr);
      if (isNaN(d.getTime())) return dateStr;

      const pad = (n) => String(n).padStart(2, "0");
      const day = pad(d.getDate());
      const month = pad(d.getMonth() + 1);
      const year = d.getFullYear();
      const hours = pad(d.getHours());
      const mins = pad(d.getMinutes());

      return `${day}.${month}.${year} ${hours}:${mins}`;
    } catch {
      return dateStr;
    }
  };

  // Tablo Kolon Tanımları
  const tableColumns = [
    {
      title: "Malzeme Kodu",
      dataIndex: "code",
      key: "code",
      render: (text) => (
        <span className="font-mono font-semibold text-content">{text}</span>
      ),
    },
    {
      title: "Malzeme Adı",
      dataIndex: "name",
      key: "name",
      render: (text, record) => (
        <div>
          <span className="font-medium text-content">{text}</span>
          <span className="ml-2 inline-block rounded bg-surface-sunken px-1.5 py-0.5 text-xs text-content-muted border border-line">
            {record.type}
          </span>
        </div>
      ),
    },
    {
      title: "Mevcut Stok",
      dataIndex: "stock",
      key: "stock",
      align: "right",
      sorter: (a, b) => a.stock - b.stock,
      render: (val) => (
        <span className="text-base font-bold text-state-info">{val} adet</span>
      ),
    },
    {
      title: "Son Yeterlilik Tarihi",
      dataIndex: "sufficiencyDate",
      key: "sufficiencyDate",
      render: (val, record) => {
        if (record.coversAll) {
          return (
            <span className="text-state-ok font-medium">
              Tüm Planı Karşılıyor
            </span>
          );
        }
        return (
          <span className="text-content font-mono">
            {formatDate(val)}
          </span>
        );
      },
    },
    {
      title: "Kalan Süre",
      dataIndex: "remainingText",
      key: "remainingText",
      sorter: (a, b) => {
        if (a.coversAll) return 1;
        if (b.coversAll) return -1;
        return (a.remainingMinutes || 0) - (b.remainingMinutes || 0);
      },
      render: (text, record) => {
        if (record.coversAll) {
          return (
            <Tag color="success" icon={<SafetyCertificateOutlined />}>
              Sürekli Karşılıyor
            </Tag>
          );
        }
        if (record.isCritical) {
          return (
            <Tag color="error" icon={<ClockCircleOutlined />}>
              {text}
            </Tag>
          );
        }
        return (
          <Tag color="processing" icon={<ClockCircleOutlined />}>
            {text}
          </Tag>
        );
      },
    },
    {
      title: "Durum",
      key: "status",
      align: "center",
      render: (_, record) => {
        if (record.isCritical) {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-rose-500/15 text-state-bad border border-rose-500/30">
              <WarningOutlined /> KRİTİK
            </span>
          );
        }
        if (record.coversAll) {
          return (
            <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-emerald-500/15 text-state-ok border border-emerald-500/30">
              <CheckCircleOutlined /> TAM KAPLAMA
            </span>
          );
        }
        return (
          <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold bg-sky-500/15 text-state-info border border-sky-500/30">
            <CheckCircleOutlined /> NORMAL
          </span>
        );
      },
    },
  ];

  return (
    <div className="w-full h-full p-4 laptop:p-6">
      {/* Üst Başlık & Eylemler */}
      <div className="mb-6 flex flex-col gap-4 laptop:flex-row laptop:items-center laptop:justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold tracking-tight text-content laptop:text-3xl">
              Stok Yeterliliği
            </h1>
            <span className="rounded-full bg-brand-500/15 px-3 py-1 text-xs font-semibold text-brand-600 dark:text-brand-300 border border-brand-500/30">
              Boyahane & Montaj
            </span>
          </div>
          <p className="mt-1 text-sm text-content-muted">
            Boyalı gövde ve gövde ön stoklarının SAP montaj planındaki siparişleri ne zamana kadar karşıladığı analizi.
          </p>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          {lastUpdated && (
            <span className="text-xs text-content-muted flex items-center gap-1">
              <ClockCircleOutlined /> Güncelleme: {lastUpdated.toLocaleTimeString("tr-TR")}
            </span>
          )}
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={fetchData}
            className="flex items-center gap-1"
          >
            Yenile
          </Button>
          <Button
            type="primary"
            icon={<FileExcelOutlined />}
            onClick={handleExportExcel}
            disabled={filteredData.length === 0}
            className="bg-brand-500 hover:bg-brand-400 border-none text-slate-950 font-bold"
          >
            Excel'e Aktar
          </Button>
        </div>
      </div>

      {/* Eski Plan Uyarısı (Legacy Plan Alert) */}
      {isLegacyPlan && (
        <Alert
          type="warning"
          showIcon
          icon={<WarningOutlined className="text-state-warn text-lg" />}
          message={
            <span className="font-semibold text-content">
              Eski Model ile Hesaplanmış Plan Uyarısı
            </span>
          }
          description="Bu plan eski model sürümü ile hesaplanmıştır. Son yeterlilik tarihleri ve kalan süreler 3 saatlik zaman kayması veya hatalı tamamlama bilgisi içerebilir. Lütfen güncel modeli çalıştırınız."
          className="mb-6 border-amber-500/40 bg-surface text-content"
        />
      )}

      {/* Hata Uyarısı */}
      {error && (
        <Alert
          type="error"
          showIcon
          message="Veri Yükleme Hatası"
          description={error}
          action={
            <Button size="small" type="primary" danger onClick={fetchData}>
              Tekrar Dene
            </Button>
          }
          className="mb-6"
        />
      )}

      {/* Metrik Kartları (Özet Paneli) */}
      <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 laptop:grid-cols-4 gap-4">
        {/* Toplam Malzeme */}
        <div className="rounded-xl border border-line bg-surface p-4 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-content-muted">
              Toplam Malzeme
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-surface-sunken text-content-muted">
              <DatabaseOutlined />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-content">{metrics.total}</span>
            <span className="text-xs text-content-muted">kalem</span>
          </div>
        </div>

        {/* Kritik Stok */}
        <div className={`rounded-xl border p-4 shadow-card transition-all ${
          metrics.criticalCount > 0
            ? "border-rose-500/50 bg-rose-500/10"
            : "border-line bg-surface"
        }`}>
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-bad">
              Kritik Ürün Sayısı
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-rose-500/20 text-state-bad">
              <WarningOutlined />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-state-bad">
              {metrics.criticalCount}
            </span>
            <span className="text-xs text-state-bad font-medium">
              {metrics.criticalCount > 0 ? "dikkat gerekiyor" : "kritik ürün yok"}
            </span>
          </div>
        </div>

        {/* Normal Stok */}
        <div className="rounded-xl border border-line bg-surface p-4 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-info">
              Normal Stok
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-sky-500/20 text-state-info">
              <CheckCircleOutlined />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-state-info">{metrics.normalCount}</span>
            <span className="text-xs text-content-muted">kalem</span>
          </div>
        </div>

        {/* Tüm Planı Karşılıyor */}
        <div className="rounded-xl border border-line bg-surface p-4 shadow-card">
          <div className="flex items-center justify-between">
            <span className="text-xs font-semibold uppercase tracking-wider text-state-ok">
              Tüm Planı Karşılıyor
            </span>
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-emerald-500/20 text-state-ok">
              <SafetyCertificateOutlined />
            </div>
          </div>
          <div className="mt-3 flex items-baseline gap-2">
            <span className="text-3xl font-extrabold text-state-ok">
              {metrics.coversAllCount}
            </span>
            <span className="text-xs text-content-muted">stok tükenmiyor</span>
          </div>
        </div>
      </div>

      {/* Araç Çubuğu & Filtreler */}
      <div className="mb-6 rounded-xl border border-line bg-surface p-4 shadow-card">
        <div className="flex flex-col gap-4 laptop:flex-row laptop:items-center laptop:justify-between">
          {/* Arama Input */}
          <div className="w-full laptop:w-72">
            <Input
              placeholder="Malzeme adı veya kodu ara..."
              prefix={<SearchOutlined className="text-content-faint" />}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              allowClear
            />
          </div>

          {/* Filtre Grupları */}
          <div className="flex flex-wrap items-center gap-4">
            {/* Malzeme Tipi */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-content-muted flex items-center gap-1">
                <FilterOutlined /> Tip:
              </span>
              <Radio.Group
                value={typeFilter}
                onChange={(e) => setTypeFilter(e.target.value)}
                optionType="button"
                buttonStyle="solid"
                size="small"
              >
                <Radio.Button value="ALL">Hepsi</Radio.Button>
                <Radio.Button value="GOVDE">Gövde</Radio.Button>
                <Radio.Button value="GOVDE_ON">Gövde Ön</Radio.Button>
              </Radio.Group>
            </div>

            {/* Durum Filtresi */}
            <div className="flex items-center gap-2">
              <span className="text-xs text-content-muted">Durum:</span>
              <Select
                value={statusFilter}
                onChange={(val) => setStatusFilter(val)}
                size="small"
                className="w-44"
                options={[
                  { label: "Tüm Durumlar", value: "ALL" },
                  { label: "🚨 Kritik Stok", value: "CRITICAL" },
                  { label: "🔹 Normal Stok", value: "NORMAL" },
                  { label: "✅ Tüm Planı Karşılıyor", value: "COVERS_ALL" },
                ]}
              />
            </div>

            {/* Görünüm Modu Değiştirici (Kart / Tablo) */}
            <Radio.Group
              value={viewMode}
              onChange={(e) => setViewMode(e.target.value)}
              optionType="button"
              buttonStyle="solid"
              size="small"
            >
              <Radio.Button value="GRID">
                <AppstoreOutlined /> Kartlar
              </Radio.Button>
              <Radio.Button value="TABLE">
                <UnorderedListOutlined /> Tablo
              </Radio.Button>
            </Radio.Group>
          </div>
        </div>
      </div>

      {/* İçerik Alanı: Yükleniyor / Liste / Kartlar */}
      {loading ? (
        <div className="flex h-64 flex-col items-center justify-center gap-3 rounded-xl border border-line bg-surface">
          <Spin size="large" />
          <span className="text-sm text-content-muted">Stok yeterlilik verileri yükleniyor...</span>
        </div>
      ) : filteredData.length === 0 ? (
        <div className="flex h-64 flex-col items-center justify-center gap-2 rounded-xl border border-line bg-surface text-center">
          <InfoCircleOutlined className="text-3xl text-content-faint" />
          <span className="text-base font-semibold text-content">Aramaya uygun malzeme bulunamadı</span>
          <span className="text-xs text-content-muted">
            Filtreleri veya arama terimini değiştirerek tekrar deneyebilirsiniz.
          </span>
        </div>
      ) : viewMode === "TABLE" ? (
        /* Tablo Görünümü */
        <div className="rounded-xl border border-line bg-surface overflow-hidden shadow-card">
          <Table
            dataSource={filteredData}
            columns={tableColumns}
            rowKey="id"
            pagination={{ pageSize: 15, showSizeChanger: true }}
          />
        </div>
      ) : (
        /* Kart Görünümü (Grid) */
        <div className="grid grid-cols-1 md:grid-cols-2 laptop:grid-cols-3 xl:grid-cols-4 gap-4">
          {filteredData.map((item) => {
            const isCrit = item.isCritical;
            const isFull = item.coversAll;

            return (
              <div
                key={item.id}
                className={`relative flex flex-col justify-between rounded-xl border p-5 shadow-card transition-all duration-200 hover:-translate-y-1 hover:shadow-raised ${
                  isCrit
                    ? "border-rose-500/40 bg-rose-500/5"
                    : isFull
                    ? "border-emerald-500/30 bg-emerald-500/5"
                    : "border-line bg-surface"
                }`}
              >
                {/* Sol Üst Status İkon Çubuğu */}
                <div className="flex items-start justify-between gap-2 mb-3">
                  <div>
                    <span className="inline-block rounded bg-surface-sunken px-2 py-0.5 font-mono text-xs font-semibold text-content border border-line">
                      {item.code}
                    </span>
                    <h3 className="mt-1.5 text-base font-bold text-content line-clamp-1">
                      {item.name}
                    </h3>
                    <span className="text-2xs font-semibold uppercase tracking-wider text-content-muted">
                      {item.type}
                    </span>
                  </div>

                  {/* Rozet */}
                  {isCrit ? (
                    <Tag color="error" className="m-0 font-bold px-2 py-0.5">
                      KRİTİK
                    </Tag>
                  ) : isFull ? (
                    <Tag color="success" className="m-0 font-bold px-2 py-0.5">
                      TÜM PLAN
                    </Tag>
                  ) : (
                    <Tag color="processing" className="m-0 font-semibold px-2 py-0.5">
                      NORMAL
                    </Tag>
                  )}
                </div>

                {/* Kart Gövdesi */}
                <div className="my-3 space-y-3 border-t border-b border-line py-3">
                  {/* Mevcut Stok */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-content-muted">Mevcut Stok:</span>
                    <span className="text-lg font-bold text-state-info">
                      {item.stock} <span className="text-xs font-normal text-content-muted">adet</span>
                    </span>
                  </div>

                  {/* Son Yeterlilik Tarihi */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-content-muted">Son Yeterlilik:</span>
                    <span className="text-xs font-semibold font-mono text-content">
                      {isFull ? (
                        <span className="text-state-ok font-bold">Stok Tükenmiyor</span>
                      ) : (
                        formatDate(item.sufficiencyDate)
                      )}
                    </span>
                  </div>

                  {/* Kalan Süre */}
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-content-muted">Kalan Süre:</span>
                    <span
                      className={`text-sm font-bold ${
                        isCrit
                          ? "text-state-bad animate-pulse"
                          : isFull
                          ? "text-state-ok"
                          : "text-content"
                      }`}
                    >
                      {item.remainingText}
                    </span>
                  </div>
                </div>

                {/* Kart Alt Çubuğu (Görsel Durum Çubuğu) */}
                <div className="w-full">
                  <div className="h-1.5 w-full rounded-full bg-surface-sunken border border-line overflow-hidden">
                    <div
                      className={`h-full rounded-full transition-all duration-500 ${
                        isCrit
                          ? "w-1/4 bg-state-bad"
                          : isFull
                          ? "w-full bg-state-ok"
                          : "w-2/3 bg-state-info"
                      }`}
                    />
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
